// ============================================================================
// HRIS Compliance Data - Federal Forms, I-9 Documents, Policy Templates
// ============================================================================

import { v4 as uuidv4 } from 'uuid';

// ============================================================================
// I-9 ACCEPTABLE DOCUMENTS
// ============================================================================

// List A - Documents that Establish Both Identity and Employment Authorization
export const I9_LIST_A_DOCUMENTS = [
  {
    code: 'US_PASSPORT',
    name: 'U.S. Passport',
    description: 'U.S. Passport (unexpired or expired)',
    hasExpiration: true,
    hasDocumentNumber: true,
  },
  {
    code: 'US_PASSPORT_CARD',
    name: 'U.S. Passport Card',
    description: 'U.S. Passport Card (unexpired or expired)',
    hasExpiration: true,
    hasDocumentNumber: true,
  },
  {
    code: 'PERM_RESIDENT_CARD',
    name: 'Permanent Resident Card (Green Card)',
    description: 'Permanent Resident Card or Alien Registration Receipt Card (Form I-551)',
    hasExpiration: true,
    hasDocumentNumber: true,
    alternateNames: ['Green Card', 'Form I-551'],
  },
  {
    code: 'FOREIGN_PASSPORT_I551',
    name: 'Foreign Passport with I-551 Stamp',
    description: 'Foreign passport that contains a temporary I-551 stamp or temporary I-551 printed notation on a machine-readable immigrant visa',
    hasExpiration: true,
    hasDocumentNumber: true,
  },
  {
    code: 'EAD_CARD',
    name: 'Employment Authorization Document (EAD)',
    description: 'Employment Authorization Document that contains a photograph (Form I-766)',
    hasExpiration: true,
    hasDocumentNumber: true,
    alternateNames: ['Form I-766', 'Work Permit'],
  },
  {
    code: 'FOREIGN_PASSPORT_I94',
    name: 'Foreign Passport with I-94',
    description: 'Foreign passport with Form I-94 or Form I-94A with Arrival-Departure Record, and containing an endorsement to work',
    hasExpiration: true,
    hasDocumentNumber: true,
  },
  {
    code: 'FSM_PASSPORT',
    name: 'FSM/RMI Passport with I-94',
    description: 'Passport from the Federated States of Micronesia (FSM) or the Republic of the Marshall Islands (RMI) with Form I-94 or Form I-94A',
    hasExpiration: true,
    hasDocumentNumber: true,
  },
];

// List B - Documents that Establish Identity
export const I9_LIST_B_DOCUMENTS = [
  {
    code: 'DRIVERS_LICENSE',
    name: "Driver's License",
    description: "Driver's license or ID card issued by a State or outlying possession of the United States",
    hasExpiration: true,
    hasDocumentNumber: true,
    requiresPhoto: true,
  },
  {
    code: 'STATE_ID',
    name: 'State ID Card',
    description: 'ID card issued by federal, state or local government agencies or entities, provided it contains a photograph',
    hasExpiration: true,
    hasDocumentNumber: true,
    requiresPhoto: true,
  },
  {
    code: 'SCHOOL_ID',
    name: 'School ID Card',
    description: 'School ID card with a photograph',
    hasExpiration: false,
    hasDocumentNumber: true,
    requiresPhoto: true,
  },
  {
    code: 'VOTER_REGISTRATION',
    name: 'Voter Registration Card',
    description: 'Voter registration card',
    hasExpiration: false,
    hasDocumentNumber: false,
  },
  {
    code: 'US_MILITARY_CARD',
    name: 'U.S. Military Card',
    description: 'U.S. Military card or draft record',
    hasExpiration: true,
    hasDocumentNumber: true,
  },
  {
    code: 'MILITARY_DEPENDENT_ID',
    name: 'Military Dependent ID',
    description: "Military dependent's ID card",
    hasExpiration: true,
    hasDocumentNumber: true,
  },
  {
    code: 'USCG_MERCHANT_MARINER',
    name: 'USCG Merchant Mariner Card',
    description: 'U.S. Coast Guard Merchant Mariner Card',
    hasExpiration: true,
    hasDocumentNumber: true,
  },
  {
    code: 'NATIVE_AMERICAN_TRIBAL',
    name: 'Native American Tribal Document',
    description: 'Native American tribal document',
    hasExpiration: false,
    hasDocumentNumber: true,
  },
  {
    code: 'CANADIAN_DRIVERS_LICENSE',
    name: "Canadian Driver's License",
    description: "Driver's license issued by a Canadian government authority",
    hasExpiration: true,
    hasDocumentNumber: true,
    requiresPhoto: true,
  },
];

// List B - Documents for persons under 18 who cannot present above documents
export const I9_LIST_B_MINOR_DOCUMENTS = [
  {
    code: 'SCHOOL_RECORD',
    name: 'School Record or Report Card',
    description: 'School record or report card (for persons under age 18)',
    hasExpiration: false,
    hasDocumentNumber: false,
    minorOnly: true,
  },
  {
    code: 'CLINIC_DOCTOR_HOSPITAL_RECORD',
    name: 'Clinic, Doctor, or Hospital Record',
    description: 'Clinic, doctor, or hospital record (for persons under age 18)',
    hasExpiration: false,
    hasDocumentNumber: false,
    minorOnly: true,
  },
  {
    code: 'DAY_CARE_NURSERY_RECORD',
    name: 'Day-Care or Nursery School Record',
    description: 'Day-care or nursery school record (for persons under age 18)',
    hasExpiration: false,
    hasDocumentNumber: false,
    minorOnly: true,
  },
];

// List C - Documents that Establish Employment Authorization
export const I9_LIST_C_DOCUMENTS = [
  {
    code: 'SSN_CARD_UNRESTRICTED',
    name: 'Social Security Card (Unrestricted)',
    description: 'A Social Security Account Number card (unrestricted)',
    hasExpiration: false,
    hasDocumentNumber: true,
    restrictions: ['NOT VALID FOR EMPLOYMENT restriction NOT present'],
  },
  {
    code: 'CERT_BIRTH_ABROAD',
    name: 'Certification of Birth Abroad (FS-545)',
    description: 'Certification of Birth Abroad issued by the Department of State (Form FS-545)',
    hasExpiration: false,
    hasDocumentNumber: true,
  },
  {
    code: 'CERT_REPORT_BIRTH',
    name: 'Certification of Report of Birth (DS-1350)',
    description: 'Certification of Report of Birth issued by the Department of State (Form DS-1350)',
    hasExpiration: false,
    hasDocumentNumber: true,
  },
  {
    code: 'CONSULAR_REPORT_BIRTH',
    name: 'Consular Report of Birth Abroad (FS-240)',
    description: 'Consular Report of Birth Abroad (Form FS-240)',
    hasExpiration: false,
    hasDocumentNumber: true,
  },
  {
    code: 'US_BIRTH_CERTIFICATE',
    name: 'U.S. Birth Certificate',
    description: 'Original or certified copy of birth certificate issued by a State, county, municipal authority, or territory of the United States bearing an official seal',
    hasExpiration: false,
    hasDocumentNumber: true,
  },
  {
    code: 'NATIVE_AMERICAN_TRIBAL_DOC',
    name: 'Native American Tribal Document',
    description: 'Native American tribal document',
    hasExpiration: false,
    hasDocumentNumber: true,
  },
  {
    code: 'US_CITIZEN_ID_CARD',
    name: 'U.S. Citizen ID Card (I-197)',
    description: 'U.S. Citizen ID Card (Form I-197)',
    hasExpiration: false,
    hasDocumentNumber: true,
  },
  {
    code: 'RESIDENT_CITIZEN_ID',
    name: 'Identification Card for Resident Citizen (I-179)',
    description: 'Identification Card for Use of Resident Citizen in the United States (Form I-179)',
    hasExpiration: false,
    hasDocumentNumber: true,
  },
  {
    code: 'EAD_USCIS',
    name: 'Employment Authorization Document (USCIS)',
    description: 'Employment authorization document issued by the Department of Homeland Security',
    hasExpiration: true,
    hasDocumentNumber: true,
  },
];

// Get all I-9 documents as flat list with list type
export const getAllI9Documents = () => {
  return [
    ...I9_LIST_A_DOCUMENTS.map(d => ({ ...d, listType: 'A' })),
    ...I9_LIST_B_DOCUMENTS.map(d => ({ ...d, listType: 'B' })),
    ...I9_LIST_B_MINOR_DOCUMENTS.map(d => ({ ...d, listType: 'B' })),
    ...I9_LIST_C_DOCUMENTS.map(d => ({ ...d, listType: 'C' })),
  ];
};

// ============================================================================
// I-9 FORM CONFIGURATION
// ============================================================================

export const I9_CITIZENSHIP_STATUS = [
  { value: 'citizen', label: 'A citizen of the United States' },
  { value: 'noncitizen_national', label: 'A noncitizen national of the United States' },
  { value: 'permanent_resident', label: 'A lawful permanent resident (Alien Registration Number/USCIS Number)' },
  { value: 'alien_authorized', label: 'An alien authorized to work until (expiration date)' },
];

export const I9_PREPARER_TRANSLATOR = [
  { value: 'self', label: 'I did not use a preparer or translator' },
  { value: 'preparer', label: 'A preparer(s) and/or translator(s) assisted the employee in completing Section 1' },
];

// ============================================================================
// FEDERAL FORM DEFINITIONS
// ============================================================================

export const FEDERAL_FORMS = {
  I9: {
    formCode: 'I-9',
    formName: 'Employment Eligibility Verification',
    description: 'Form I-9 is used to verify the identity and employment authorization of individuals hired for employment in the United States.',
    category: 'federal',
    jurisdiction: 'federal',
    version: '10/21/2019',
    ombNumber: '1615-0047',
    expirationDate: '10/31/2025',
    sections: [
      {
        id: 'section1',
        title: 'Section 1. Employee Information and Attestation',
        completedBy: 'employee',
        deadline: 'First day of employment',
        description: 'Employees must complete and sign Section 1 of Form I-9 no later than the first day of employment.',
      },
      {
        id: 'section2',
        title: 'Section 2. Employer or Authorized Representative Review and Verification',
        completedBy: 'employer',
        deadline: 'Within 3 business days of employment start',
        description: 'Employers must complete Section 2 by examining evidence of identity and employment authorization within 3 business days of the employee\'s first day of employment.',
      },
      {
        id: 'section3',
        title: 'Section 3. Reverification and Rehires',
        completedBy: 'employer',
        deadline: 'Before expiration or upon rehire',
        description: 'Employers must reverify employment authorization for employees whose employment authorization or documentation of employment authorization has expired.',
      },
    ],
  },
  W4: {
    formCode: 'W-4',
    formName: "Employee's Withholding Certificate",
    description: 'Form W-4 tells your employer how much federal income tax to withhold from your paycheck.',
    category: 'federal',
    jurisdiction: 'federal',
    version: '2024',
    ombNumber: '1545-0074',
  },
};

// ============================================================================
// HRIS FORM CATEGORIES
// ============================================================================

export const FORM_CATEGORIES = {
  FEDERAL_TAX: { code: 'federal_tax', label: 'Federal Tax Forms', order: 1 },
  FEDERAL_EMPLOYMENT: { code: 'federal_employment', label: 'Federal Employment Forms', order: 2 },
  STATE_TAX: { code: 'state_tax', label: 'State Tax Forms', order: 3 },
  STATE_NOTICES: { code: 'state_notices', label: 'State Mandatory Notices', order: 4 },
  POLICY: { code: 'policy', label: 'Policy Acknowledgments', order: 5 },
  AGREEMENT: { code: 'agreement', label: 'Agreements', order: 6 },
  COMPANY: { code: 'company', label: 'Company Forms', order: 7 },
};

// ============================================================================
// FORM STATUS DEFINITIONS
// ============================================================================

export const FORM_STATUS = {
  NOT_STARTED: { value: 'not_started', label: 'Not Started', color: 'gray' },
  IN_PROGRESS: { value: 'in_progress', label: 'In Progress', color: 'blue' },
  PENDING_SIGNATURE: { value: 'pending_signature', label: 'Pending Signature', color: 'amber' },
  SUBMITTED: { value: 'submitted', label: 'Submitted', color: 'purple' },
  PENDING_VERIFICATION: { value: 'pending_verification', label: 'Pending Verification', color: 'orange' },
  VERIFIED: { value: 'verified', label: 'Verified', color: 'green' },
  REJECTED: { value: 'rejected', label: 'Rejected', color: 'red' },
  EXPIRED: { value: 'expired', label: 'Expired', color: 'red' },
};

// ============================================================================
// I-9 VERIFICATION STATUS
// ============================================================================

export const I9_STATUS = {
  NOT_STARTED: 'not_started',
  SECTION1_PENDING: 'section1_pending',
  SECTION1_COMPLETE: 'section1_complete',
  PENDING_SECTION2: 'pending_section2', // Alias for backward compatibility
  SECTION2_PENDING: 'section2_pending',
  SECTION2_OVERDUE: 'section2_overdue',
  COMPLETE: 'complete',
  REVERIFICATION_NEEDED: 'reverification_needed',
};

// Helper to get display labels for I9 status
export const I9_STATUS_LABELS = {
  [I9_STATUS.NOT_STARTED]: { label: 'Not Started', color: 'gray' },
  [I9_STATUS.SECTION1_PENDING]: { label: 'Section 1 Pending', color: 'gray' },
  [I9_STATUS.SECTION1_COMPLETE]: { label: 'Section 1 Complete', color: 'blue' },
  [I9_STATUS.PENDING_SECTION2]: { label: 'Section 2 Pending', color: 'amber' },
  [I9_STATUS.SECTION2_PENDING]: { label: 'Section 2 Pending', color: 'amber' },
  [I9_STATUS.SECTION2_OVERDUE]: { label: 'Section 2 Overdue', color: 'red' },
  [I9_STATUS.COMPLETE]: { label: 'Complete', color: 'green' },
  [I9_STATUS.REVERIFICATION_NEEDED]: { label: 'Reverification Needed', color: 'orange' },
};

// ============================================================================
// E-VERIFY STATUS
// ============================================================================

export const EVERIFY_STATUS = {
  NOT_SUBMITTED: { value: 'not_submitted', label: 'Not Submitted' },
  PENDING: { value: 'pending', label: 'Pending' },
  EMPLOYMENT_AUTHORIZED: { value: 'employment_authorized', label: 'Employment Authorized' },
  TENTATIVE_NONCONFIRMATION: { value: 'tnc', label: 'Tentative Nonconfirmation' },
  CASE_IN_CONTINUANCE: { value: 'continuance', label: 'Case in Continuance' },
  FINAL_NONCONFIRMATION: { value: 'fnc', label: 'Final Nonconfirmation' },
  CLOSED: { value: 'closed', label: 'Closed' },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate I-9 Section 2 due date (3 business days from hire date)
 * @returns {Date} Returns a Date object
 */
export function calculateI9Section2DueDate(hireDate) {
  const date = new Date(hireDate);
  let businessDays = 0;

  while (businessDays < 3) {
    date.setDate(date.getDate() + 1);
    const dayOfWeek = date.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessDays++;
    }
  }

  return date;
}

/**
 * Check if I-9 Section 2 is overdue
 * @param {Date|number} dueDate - Date object or timestamp
 */
export function isI9Section2Overdue(dueDate) {
  const dueDateMs = dueDate instanceof Date ? dueDate.getTime() : dueDate;
  return Date.now() > dueDateMs;
}

/**
 * Get days until I-9 Section 2 due
 * @param {Date|number} dueDate - Date object or timestamp
 */
export function getDaysUntilI9Due(dueDate) {
  const dueDateMs = dueDate instanceof Date ? dueDate.getTime() : dueDate;
  const diff = dueDateMs - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * Get document by code
 */
export function getI9DocumentByCode(code) {
  return getAllI9Documents().find(d => d.code === code);
}

/**
 * Validate I-9 document combination
 * Employee must present either: 1 List A document OR (1 List B + 1 List C document)
 */
export function validateI9Documents(documents) {
  const listA = documents.filter(d => d.listType === 'A');
  const listB = documents.filter(d => d.listType === 'B');
  const listC = documents.filter(d => d.listType === 'C');

  if (listA.length >= 1) {
    return { valid: true, method: 'List A' };
  }

  if (listB.length >= 1 && listC.length >= 1) {
    return { valid: true, method: 'List B + List C' };
  }

  return {
    valid: false,
    error: 'Employee must present either one List A document OR one List B document AND one List C document.'
  };
}

export default {
  I9_LIST_A_DOCUMENTS,
  I9_LIST_B_DOCUMENTS,
  I9_LIST_B_MINOR_DOCUMENTS,
  I9_LIST_C_DOCUMENTS,
  getAllI9Documents,
  I9_CITIZENSHIP_STATUS,
  I9_PREPARER_TRANSLATOR,
  FEDERAL_FORMS,
  FORM_CATEGORIES,
  FORM_STATUS,
  I9_STATUS,
  EVERIFY_STATUS,
  calculateI9Section2DueDate,
  isI9Section2Overdue,
  getDaysUntilI9Due,
  getI9DocumentByCode,
  validateI9Documents,
};

// ============================================================================
// STATE TAX CONFIGURATION
// ============================================================================

export const STATE_TAX_CONFIG = {
  // NO INCOME TAX STATES
  AK: { hasIncomeTax: false, formName: 'None' },
  FL: { hasIncomeTax: false, formName: 'None' },
  NV: { hasIncomeTax: false, formName: 'None' },
  SD: { hasIncomeTax: false, formName: 'None' },
  TN: { hasIncomeTax: false, formName: 'None' },
  TX: { hasIncomeTax: false, formName: 'None' },
  WA: { hasIncomeTax: false, formName: 'None' },
  WY: { hasIncomeTax: false, formName: 'None' },
  NH: { hasIncomeTax: false, formName: 'None' }, // Interest/Divs only

  // FEDERAL W-4 STATES (Uses Federal W-4)
  CO: { hasIncomeTax: true, usesFederalW4: true, formName: 'Federal W-4' },
  DE: { hasIncomeTax: true, usesFederalW4: true, formName: 'Federal W-4' },
  ID: { hasIncomeTax: true, usesFederalW4: true, formName: 'Federal W-4' },
  MT: { hasIncomeTax: true, usesFederalW4: true, formName: 'Federal W-4' },
  NE: { hasIncomeTax: true, usesFederalW4: true, formName: 'Federal W-4' },
  NM: { hasIncomeTax: true, usesFederalW4: true, formName: 'Federal W-4' },
  ND: { hasIncomeTax: true, usesFederalW4: true, formName: 'Federal W-4' },
  OK: { hasIncomeTax: true, usesFederalW4: true, formName: 'Federal W-4' },
  SC: { hasIncomeTax: true, usesFederalW4: true, formName: 'Federal W-4' },
  UT: { hasIncomeTax: true, usesFederalW4: true, formName: 'Federal W-4' },

  // SPECIFIC STATE FORMS
  CA: { hasIncomeTax: true, formCode: 'DE-4', formName: 'California DE-4', component: 'FormDE4' },
  NY: { hasIncomeTax: true, formCode: 'IT-2104', formName: 'New York IT-2104', downloadUrl: 'https://www.tax.ny.gov/pdf/current_forms/it/it2104_fill_in.pdf' },
  IL: { hasIncomeTax: true, formCode: 'IL-W-4', formName: 'Illinois IL-W-4', downloadUrl: 'https://www2.illinois.gov/rev/forms/withholding/Documents/current/il-w-4.pdf' },
  // ... others map to Generic
};

export const getStateTaxRequirements = (stateCode) => {
  const code = (stateCode || '').toUpperCase();
  const config = STATE_TAX_CONFIG[code];

  if (!config) {
    if (!code) {
      // If no state, assume required (safe default)
      return {
        hasIncomeTax: true,
        formName: 'State Tax Withholding',
        isGeneric: true
      };
    }
    // Default to generic state tax requirement if state unknown
    return {
      hasIncomeTax: true,
      formName: `${code} State Tax Withholding`,
      isGeneric: true
    };
  }

  return config;
};
